// #1585 v2 — Tutorial obligatorio "Tu primer documento" (.claude/specs/1585-tutorial-primer-documento/).
// Decisiones PURAS (sin React) para poder testearlas y citarlas desde Turno.js.

// El sistema existe recién cuando el backend expone `documentos_generados` en /me. Si el front sube
// antes que el back, el campo no viene y todo queda como hoy (intro, modo guiado, ejemplo).
export const sistemaTutorialActivo = (user) =>
  Boolean(user && typeof user.documentos_generados === "number" && !user.is_superuser);

// Cubre R1 y R5: obligatorio con 2 documentos o menos y sin haberlo completado. Nunca para un
// superuser ni cuando se mira el turno de otro.
export const decidirTutorialObligatorio = (user, { selectedOwner = null } = {}) =>
  sistemaTutorialActivo(user) &&
  selectedOwner == null &&
  user.documentos_generados <= 2 &&
  !user.tutorial_primer_doc_completado_at;

// #1585 v3 (Sebastián 12-oct): el celular es la primera aproximación (tutorial propio y breve, sin el copiar
// y pegar) y termina en "Ingresa al computador del hospital". Por eso, quien lo vio en el celular ve en el
// computador el tutorial COMPLETO: "no ahondar en detalles que sí hará en el tutorial del pc". Solo va
// directo a la práctica quien ya terminó el video del COMPUTADOR (`video_pc_at`, marca aparte en el
// servidor) y se salió a medias. El aparato de /me es el de la PRIMERA vez y no se pisa: con él, quien
// empezó en el celular volvía a ver el video entero en cada intento (revisor 12-oct, B1).
// Cubre R13, R14 — .claude/specs/1585-tutorial-primer-documento/
export const modoTutorialObligatorio = (user, { movil = false } = {}) =>
  !movil && user?.tutorial_primer_doc_video_pc_at ? "practica" : "obligatorio";

// La página estática viaja tal cual se aprobó (mockup E); React solo la monta a pantalla completa.
// `fin=1`: en el celular, quien ya vio el video vuelve directo a la pantalla final. `correo=0`: lo vio en
// el computador, así que no se le mandó el correo y la pantalla no lo promete.
// `inst=1|0` (#1660 R17): la página vive en un iframe y no sabe si la app corre instalada en el padre, así
// que el padre se lo dice. Solo `movil && !instalada` da 1 (tarjeta "Déjala como app en tu celular" en la
// pantalla final del celular); una tablet con ventana angosta cuenta como `movil`, y el primer computador
// (`movil: false`) siempre da 0. Sin el parámetro la página no dibuja la tarjeta: la página aprobada queda igual.
// Cubre R11, R17 — .claude/specs/1660-tutorial-primer-ingreso-computador/
// `computador` (R20, por hardware) apaga la tarjeta aunque `movil` (por ancho de ventana) sea verdadero: un
// computador con la ventana bajo 768 px carga `d=m` y ahí "Ver cómo" solo diría "Estás en un computador"
// (revisor 13-oct, F1). Cubre R17 — .claude/specs/1660-tutorial-primer-ingreso-computador/
// `camas=1` (#1859): al final del primer documento en el computador la página marca con una flecha el botón
// «Sincronizar camas»; el padre lo manda solo si a la cuenta le corresponde ese tutorial en este aparato (Turno.js).
// `sinc=1` (#1859, 30-oct): el final cierra con «Sincronizar mis camas ahora» y un enlace «Lo hago después» en vez de
// «Ir a mi Turno»; el padre lo manda solo si sabe abrir Sincronizar camas.
// `alt=1` (#1889, R4): el final del tutorial 1 suma las tres salidas bajo ese botón. Solo junto a `sinc` (las salidas
// cuelgan de «Sincronizar mis camas ahora»); el padre lo manda solo si la cuenta tiene `ha_generado === false`.
// Cubre R4 — .claude/specs/1889-salidas-tutorial1/
export const urlTutorialPrimerDoc = ({ movil, tema, modo, videoVisto = false, videoAparato = "", instalada = false, computador = false, camas = false, sinc = false, salidas = false }) => {
  const m = ["repaso", "practica"].includes(modo) ? modo : "obligatorio";
  let extra = "";
  if (movil && videoVisto && m !== "repaso") extra += `&fin=1${videoAparato === "movil" ? "" : "&correo=0"}`;
  return `/tutorial/primer-documento.html?d=${movil ? "m" : "d"}&theme=${tema === "light" ? "light" : "dark"}&modo=${m}${extra}&inst=${movil && !instalada && !computador ? 1 : 0}${camas ? "&camas=1" : ""}${sinc ? "&sinc=1" : ""}${sinc && salidas ? "&alt=1" : ""}`;
};

// ── #1660 · Tutorial al primer ingreso desde el computador ──
// .claude/specs/1660-tutorial-primer-ingreso-computador/

// Cubre R20 — espejo de `_TABLET` de backend/app/finetuning/device.py más el truco de iPadOS de
// `detectPlatform` (pwa-install.js): iPadOS 13+ dice "Macintosh" y se delata por el touch.
export const esTabletPorUA = (ua = "", { maxTouchPoints = 0, platform = "" } = {}) =>
  /ipad|tablet|playbook|silk|android(?!.*mobile)/i.test(ua) ||
  /iPad/i.test(platform) ||
  (/Macintosh/i.test(ua) && maxTouchPoints > 1);

// Cubre R20. `escritorio` = esEscritorio() de idle-session.js, que #1660 no cambia: el cierre de sesión por
// inactividad sigue tratando al iPad como computador a propósito; acá el iPad cuenta como celular
// (Sebastián, 13-oct: "iPad: No, como celular"). Desde #1211 (30-oct) `esEscritorio()` da false en un
// teléfono táctil que declara puntero fino (Samsung): ese teléfono deja de ver el tutorial de computador.
export const esComputadorParaTutorial = ({ escritorio, ua, maxTouchPoints, platform }) =>
  Boolean(escritorio) && !esTabletPorUA(ua, { maxTouchPoints, platform });

const tieneMarcaNula = (user) =>
  Boolean(user) && Object.prototype.hasOwnProperty.call(user, "uso_escritorio_at") && user.uso_escritorio_at === null;

// Cubre R6, R7, R10, R14. Abre el repaso solo la primera vez que la cuenta entra al Turno desde un
// computador. Criterio estricto: el campo AUSENTE (backend viejo) es "no", igual que `ha_generado === false`.
// `reintro_pendiente` NO entra: si coincide con el rescate gana el tutorial (R14). `gateDeAlta` y
// `avisoReactivada` entran como booleanos para que la función siga pura y sin imports.
export const decidirTutorialPrimerComputador = ({
  user,
  computador,
  selectedOwner = null,
  gateDeAlta = false,
  avisoReactivada = false,
  marcadaEnEstaCarga = false,
}) =>
  user?.id != null &&
  tieneMarcaNula(user) &&
  Boolean(computador) &&
  sistemaTutorialActivo(user) &&
  selectedOwner == null &&
  !decidirTutorialObligatorio(user, { selectedOwner }) &&
  // #1701 R9: completar la práctica de CÁMARA en el celular no enseña el copiar y pegar; esa cuenta (completado
  // sin `video_pc_at`) también ve el tutorial del computador la primera vez. Completar en el PC siempre deja
  // `video_pc_at` (la práctica del PC va después del video del PC). Espejo: proxima_pantalla.py `primer_computador`.
  // Cubre R9 — .claude/specs/1701-onboarding-dos-caminos/
  !(user.tutorial_primer_doc_completado_at && user.tutorial_primer_doc_video_pc_at) &&
  !gateDeAlta &&
  !avisoReactivada &&
  !marcadaEnEstaCarga;

// Cubre R9. El POST de `uso-escritorio` sale solo cuando el tutorial se ABRIÓ SOLO en un computador:
// primer computador, obligatorio o práctica. El repaso del botón "▶ Paso 1" (origen null) no marca, y
// `yaEnviado` garantiza una sola vez por carga de la página (también cubre R10: marca en memoria ANTES del POST).
export const debeMarcarUsoEscritorio = ({ user, computador, tutorialModo, tutorialOrigen, yaEnviado }) =>
  Boolean(computador) &&
  tieneMarcaNula(user) &&
  !yaEnviado &&
  (tutorialOrigen === "primer_computador" || tutorialModo === "obligatorio" || tutorialModo === "practica");

// Paquete #7 (Sebastián, 20-oct-2026): hoja de Acciones del celular, «Paso 1 mientras falte, Tutoriales después».
// Mientras la cuenta no tenga su primer documento (el tutorial «Tu primer documento» sin hacer), la hoja trae SOLO la
// fila «▶ Paso 1 · Tu primer documento» y Tutoriales muestra únicamente la Intro; cuando ya lo tiene, la fila Paso 1
// desaparece y «Tu primer documento» queda en Tutoriales como repaso. Nunca las dos a la vez. Sin ▶ Paso 1 (p. ej. un
// superusuario, que no tiene el sistema del tutorial) el repaso sigue yendo en Tutoriales. El computador no cambia.
// Cubre el hallazgo A del combo #7 — `TurnoCockpitMobileB.hojaPrimerDoc.test.jsx`.
export const entradaPrimerDocEnHoja = ({ tieneOnPaso1 = false, tieneOnTutorialPrimerDoc = false, tutorialHecho = false } = {}) => {
  const fila = Boolean(tieneOnPaso1) && !tutorialHecho;
  const celda = Boolean(tieneOnTutorialPrimerDoc) && !fila;
  return { fila, celda };
};
